<template>
  <div
    v-if="isVisible"
    :class="[
      'own-dialog',
      'flex-col',
      `own-dialog--height-${height}`,
      { 'own-dialog--anchored': anchored },
      'animate-in fade-in',
    ]"
    @mousedown="onBackgroundMouseDown"
    @click.stop="onBackgroundClick"
  >
    <OwnCard
      v-if="position === 'right'"
      v-motion-slide-visible-right
      v-bind="cardAttrs"
      class="shadow-card-default"
    >
      <slot name="full-dialog">
        <slot name="heading" :close="closeDialog" />
        <div v-if="height === 'full'" class="own-dialog__card-content">
          <slot :close="closeDialog" />
        </div>
        <slot v-else :close="closeDialog" />
        <slot name="footer" :close="closeDialog" />
      </slot>
    </OwnCard>

    <OwnCard
      v-else
      v-motion-slide-visible-bottom
      v-bind="cardAttrs"
      class="shadow-card-default"
    >
      <slot name="full-dialog">
        <slot name="heading" :close="closeDialog" />
        <div v-if="height === 'full'" class="own-dialog__card-content">
          <slot :close="closeDialog" />
        </div>
        <slot v-else :close="closeDialog" />
        <slot name="footer" :close="closeDialog" />
      </slot>
    </OwnCard>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, ref, toRef } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import { useBreakpoints } from '@/utils/useBreakpoints'

import { OwnCard } from '../OwnCard'

export type DialogSize = 'sm' | 'lg' | 'xs'
export type DialogHeight = 'auto' | 'full'
export type DialogPosition = 'center' | 'right'

const props = withDefaults(
  defineProps<{
    anchored?: boolean
    closeOnBackgroundClick?: boolean
    height?: DialogHeight
    navigationGuard?: boolean
    persistent?: boolean
    position?: DialogPosition
    size?: DialogSize
  }>(),
  {
    anchored: false,
    closeOnBackgroundClick: true,
    height: 'auto',
    navigationGuard: false,
    persistent: false,
    position: 'center',
    size: 'sm',
  }
)

// Provide the persistent state as a reactive reference for child components
provide('dialogPersistent', toRef(props, 'persistent'))

onBeforeRouteLeave((to, from) => {
  if (!props.navigationGuard) return

  const answer = window.confirm('Do you really want to leave?')
  if (!answer) return false
})

const emit = defineEmits<{ cancel: [] }>()

const isVisible = defineModel<boolean>()

// Track if mousedown occurred on background (for proper click-outside behavior)
const mouseDownOnBackground = ref(false)

const cardClasses = computed(() => [
  'own-dialog__card',
  `own-dialog__card--size-${props.size}`,
  `own-dialog__card--height-${props.height}`,
  `own-dialog__card--position-${props.position}`,
])

const { breakpoints } = useBreakpoints()

const cardRadius = computed(() => {
  if (props.anchored && props.position === 'right' && breakpoints.sm.value) {
    return 'var(--radius-modal-regular) 0 0 var(--radius-modal-regular)'
  }

  return undefined
})

const cardAttrs = computed(() => ({
  class: cardClasses.value,
  onClick: (event: MouseEvent) => {
    // Prevent click events from propagating to the dialog background
    event.stopPropagation()
  },
  onMousedown: (event: MouseEvent) => {
    // Mark that mousedown occurred inside the modal
    mouseDownOnBackground.value = false
    event.stopPropagation()
  },
  radius: cardRadius.value,
}))

const closeDialog = () => {
  isVisible.value = false
}

const onCancel = () => {
  if (!props.persistent) {
    emit('cancel')
    closeDialog()
  }
}

const onBackgroundMouseDown = () => {
  // Track that mousedown occurred on background
  mouseDownOnBackground.value = true
}

const onBackgroundClick = () => {
  // Only close if both mousedown AND mouseup (click) occurred on background
  if (props.closeOnBackgroundClick && mouseDownOnBackground.value) {
    onCancel()
  }
  // Reset for next interaction
  mouseDownOnBackground.value = false
}

/**
 * Provide the cancel handler for child components (like OwnDialogHeading).
 * This allows OwnDialogHeading to automatically call onCancel when its close button
 * is clicked, without requiring an explicit @cancel listener.
 *
 * Usage in child components:
 * - OwnDialogHeading will automatically work without @cancel prop
 * - Explicit @cancel listeners still work and take precedence
 *
 * This must come after onCancel is defined to avoid hoisting issues.
 */
provide('dialogCancel', onCancel)
</script>

<style lang="scss">
@use '@/styles/breakpoints';

.own-dialog {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 300;

  background-color: var(--color-surface-modal);
  padding: 16px;
  margin: 0;

  &--anchored {
    padding: 0;
  }

  &--height-auto {
    justify-content: center;
  }

  &__card {
    width: 100%;

    &--position-center {
      align-self: center;
    }
    &--position-right {
      align-self: flex-end;
    }

    &--height-auto {
      max-height: calc(100% - 80px);
    }
    &--height-full {
      height: 100%;
      max-height: 100%;
      justify-content: flex-start;

      .own-dialog__card-content {
        flex: 1;
        overflow-y: auto;
      }
    }

    &--size-lg {
      @include breakpoints.respond-to('lg-and-up') {
        width: 900px;
      }
    }
    &--size-sm {
      @include breakpoints.respond-to('md-and-up') {
        width: 600px;
      }
    }
    &--size-xs {
      @include breakpoints.respond-to('md-and-up') {
        width: 450px;
      }
    }
  }

  &__close {
    width: 20px;
    height: 20px;
  }
}
</style>
